// Wolf Oil Guide — renders Wolf Lubricants oil recommendations for a vehicle.
// Everything is driven by our own /api/oil/wolf/recommend endpoint — unlike the
// Liqui Moly embed there's no third-party widget to mount; the parts-finder API
// talks to Wolf directly and returns cross-linked Goldwagen matches, and this
// widget just renders the response.
//
// Config (read from the TARGET element, not the script tag):
//   #parts-wolf-oil-guide  OR  [data-parts-wolf-oil-guide]  — the container
//   data-title      Heading rendered above the panel
//                   (default "Recommended by Wolf for your vehicle")
//   data-language   Wolf API locale (default "en-ZA")
//   data-country    Wolf API country (default "ZA")
//   data-vehicle    Manual override for the search query — set this when the
//                   storefront page doesn't expose a
//                   window.__PARTS_FINDER_VEHICLE__ global or URL params.
//
// Vehicle context is read in this order (first hit wins):
//   1. window.__PARTS_FINDER_VEHICLE__  — { make, model, variant, year }
//   2. URL query params ?make=&model=&variant=&year=
//   3. data-vehicle attribute on the container
//   4. empty — user types into the search input themselves

import { apiFetch, apiJson } from '../shared/api.js';
import { loadFeatures } from '../shared/identity.js';

var _host = null;
var _panel = null;
var _searchInput = null;
var _searchBtn = null;
var _bodyEl = null;
var _language = 'en-ZA';
var _country = 'ZA';
var _title = 'Recommended by Wolf for your vehicle';
// Track the request in-flight so a fast second search cancels the first
// render — otherwise a slow first request can overwrite a newer result.
var _currentReqId = 0;
// Persisted multi-candidate list. renderResponse draws the picker from THIS, not
// from the per-response data.candidates: the candidate-specific follow-up request
// (doSearchByCandidate) doesn't return the candidates array, so drawing from it
// made the pills vanish after selecting one — with no way back. Set only on a
// free-text search (doSearch); left intact while switching candidates.
var _candidates = [];

// ---------- container ----------

function findContainer() {
	return document.getElementById('parts-wolf-oil-guide')
		|| document.querySelector('[data-parts-wolf-oil-guide]');
}

// ---------- vehicle context ----------

function readVehicleContext() {
	// 1. Global set by a sibling embed (e.g. the vehicle selector on the page)
	try {
		var g = window.__PARTS_FINDER_VEHICLE__;
		if (g && typeof g === 'object') {
			var parts = [g.year, g.make, g.model, g.variant].filter(Boolean).map(String);
			if (parts.length) return parts.join(' ').trim();
		}
	} catch (e) { /* ignore */ }
	// 2. URL params
	try {
		var qs = new URLSearchParams(window.location.search);
		var p = [qs.get('year'), qs.get('make'), qs.get('model'), qs.get('variant')]
			.filter(Boolean).map(String);
		if (p.length) return p.join(' ').trim();
	} catch (e) { /* ignore */ }
	// 3. Container data-vehicle
	var v = _host && _host.getAttribute('data-vehicle');
	if (v && v.trim()) return v.trim();
	return '';
}

// ---------- panel skeleton ----------

function buildPanel() {
	var panel = document.createElement('div');
	panel.className = 'pfwog pfwog-panel';

	var header = document.createElement('div');
	header.className = 'pfwog-header';
	var accent = document.createElement('div');
	accent.className = 'pfwog-header-accent';
	var titleEl = document.createElement('div');
	titleEl.className = 'pfwog-header-title';
	titleEl.textContent = _title;
	var brandEl = document.createElement('div');
	brandEl.className = 'pfwog-header-brand';
	brandEl.textContent = 'Wolf Lubricants';
	header.appendChild(accent);
	header.appendChild(titleEl);
	header.appendChild(brandEl);

	var searchbar = document.createElement('div');
	searchbar.className = 'pfwog-searchbar';
	_searchInput = document.createElement('input');
	_searchInput.type = 'text';
	_searchInput.className = 'pfwog-input';
	_searchInput.placeholder = 'e.g. 2020 Ford Everest 2.2 TDCi';
	_searchInput.setAttribute('aria-label', 'Vehicle description');
	_searchBtn = document.createElement('button');
	_searchBtn.type = 'button';
	_searchBtn.className = 'pfwog-btn';
	_searchBtn.textContent = 'Search';
	searchbar.appendChild(_searchInput);
	searchbar.appendChild(_searchBtn);

	_bodyEl = document.createElement('div');
	_bodyEl.className = 'pfwog-body';

	panel.appendChild(header);
	panel.appendChild(searchbar);
	panel.appendChild(_bodyEl);

	_searchBtn.addEventListener('click', function () { doSearch(_searchInput.value); });
	_searchInput.addEventListener('keydown', function (e) {
		if (e.key === 'Enter') { e.preventDefault(); doSearch(_searchInput.value); }
	});

	return panel;
}

// ---------- render states ----------

function renderLoading() {
	_bodyEl.innerHTML = '';
	var wrap = document.createElement('div');
	wrap.className = 'pfwog-loader';
	var sp = document.createElement('div');
	sp.className = 'pfwog-spinner';
	var t = document.createElement('span');
	t.textContent = 'Looking up Wolf recommendations…';
	wrap.appendChild(sp);
	wrap.appendChild(t);
	_bodyEl.appendChild(wrap);
}

function renderMessage(kind, text) {
	_bodyEl.innerHTML = '';
	var d = document.createElement('div');
	d.className = 'pfwog-notice ' + kind;
	d.textContent = text;
	_bodyEl.appendChild(d);
}

function renderEmpty(message) {
	_bodyEl.innerHTML = '';
	var d = document.createElement('div');
	d.className = 'pfwog-empty';
	d.textContent = message || 'Enter a vehicle above to see Wolf recommendations.';
	_bodyEl.appendChild(d);
}

function renderResponse(data, activeCandidateId) {
	_bodyEl.innerHTML = '';

	if (data && data.unavailable) {
		var un = document.createElement('div');
		un.className = 'pfwog-notice error';
		un.textContent = (data.reason || 'Wolf recommendations are temporarily unavailable.');
		_bodyEl.appendChild(un);
		return;
	}

	// Narrowing notice — when Wolf couldn't match the full query so the API
	// fell back to a shorter one. Show the shorter query so the user can tell
	// we didn't match on their full string.
	if (data && data.query && data.resolvedQuery && data.query !== data.resolvedQuery) {
		var n = document.createElement('div');
		n.className = 'pfwog-notice narrowed';
		n.textContent = 'No exact match for "' + data.query + '", showing results for "' + data.resolvedQuery + '".';
		_bodyEl.appendChild(n);
	}

	// Candidate picker — drawn from the PERSISTED list (_candidates) so the pills
	// stay after selecting one (the candidate-specific response omits candidates).
	if (_candidates.length > 1) {
		var picker = document.createElement('div');
		picker.className = 'pfwog-picker';
		var lbl = document.createElement('div');
		lbl.className = 'pfwog-picker-label';
		lbl.textContent = _candidates.length + ' matching vehicles. Pick one:';
		picker.appendChild(lbl);
		_candidates.forEach(function (cand) {
			var isActive = cand.typeId === activeCandidateId;
			var chip = document.createElement('button');
			chip.type = 'button';
			chip.className = 'pfwog-chip' + (isActive ? ' active' : '');
			chip.textContent = cand.name;
			chip.addEventListener('click', function () {
				if (isActive) return;
				doSearchByCandidate(cand);
			});
			picker.appendChild(chip);
		});
		_bodyEl.appendChild(picker);
	} else if (data && Array.isArray(data.candidates) && data.candidates.length === 1) {
		// Single candidate — show the vehicle name as a subtle info line
		var s = document.createElement('div');
		s.className = 'pfwog-notice info';
		s.textContent = 'Matched vehicle: ' + data.candidates[0].name;
		_bodyEl.appendChild(s);
	}

	// Only show recommendations that Goldwagen actually stocks — a card
	// for a product the customer can't buy is noise. Wolf still returns
	// the full recommendation set from our API; this filter is UI-only
	// (mirrors the same filter in WolfOilGuideModal.tsx).
	var allRecs = (data && Array.isArray(data.recommendations)) ? data.recommendations : [];
	var recs = allRecs.filter(function (rec) {
		return rec && rec.goldwagenPart && rec.goldwagenPart.id;
	});
	if (recs.length === 0) {
		var e = document.createElement('div');
		e.className = 'pfwog-empty';
		e.textContent = 'No Wolf-recommended parts are stocked by Goldwagen for this vehicle.';
		_bodyEl.appendChild(e);
		return;
	}

	var grid = document.createElement('div');
	grid.className = 'pfwog-grid';
	recs.forEach(function (rec) { grid.appendChild(renderCard(rec)); });
	_bodyEl.appendChild(grid);
}

function renderCard(rec) {
	var card = document.createElement('article');
	card.className = 'pfwog-card';

	var header = document.createElement('div');
	header.className = 'pfwog-card-header';
	var brand = document.createElement('span');
	brand.className = 'pfwog-card-brand';
	brand.textContent = 'WOLF';
	header.appendChild(brand);
	var name = document.createElement('div');
	name.className = 'pfwog-card-name';
	name.textContent = rec.name || rec.partNumber || rec.wolfCode || '';
	header.appendChild(name);
	if (rec.partNumber) {
		var pn = document.createElement('div');
		pn.className = 'pfwog-card-part';
		pn.textContent = rec.partNumber;
		header.appendChild(pn);
	}
	if (rec.category) {
		var cat = document.createElement('div');
		cat.className = 'pfwog-card-cat';
		cat.textContent = rec.category;
		header.appendChild(cat);
	}
	card.appendChild(header);

	// Callers only pass recs with a valid goldwagenPart (parent filters
	// out unstocked recommendations), but we keep the null-check so the
	// card stays safe if this helper is ever reused with raw API data.
	var body = document.createElement('div');
	body.className = 'pfwog-card-body';
	var gp = rec.goldwagenPart;
	if (gp && gp.id) {
		var mBox = document.createElement('div');
		mBox.className = 'pfwog-match';
		var mLbl = document.createElement('div');
		mLbl.className = 'pfwog-match-label';
		mLbl.textContent = 'Available at Goldwagen';
		var mSku = document.createElement('div');
		mSku.className = 'pfwog-match-sku';
		mSku.textContent = gp.id;
		mBox.appendChild(mLbl);
		mBox.appendChild(mSku);
		// Primary click resolves the OE Commerce storefront product page
		// (mirrors the Liqui Moly embed + parts-finder ResultsGrid). The
		// href keeps the in-app parts-finder page as a graceful fallback for
		// no-JS and ctrl/cmd/middle-click "open in new tab".
		var link = document.createElement('a');
		link.className = 'pfwog-match-link';
		link.textContent = 'View part →';
		link.href = buildPartsFinderUrl(gp.id);
		link.target = '_top';
		link.addEventListener('click', function (e) {
			// Let modified / non-primary clicks fall through to the href.
			if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
			e.preventDefault();
			navigateToPart(gp.id);
		});
		mBox.appendChild(link);
		body.appendChild(mBox);
	}
	card.appendChild(body);

	return card;
}

function buildPartsFinderUrl(partId) {
	// Anchor into the parent site's /parts-finder route with the part
	// pre-selected. Mirrors what storefront-parts-search.js does.
	try {
		var origin = window.top && window.top.location ? window.top.location.origin : window.location.origin;
		return origin + '/parts-finder?partId=' + encodeURIComponent(partId);
	} catch (e) {
		return '/parts-finder?partId=' + encodeURIComponent(partId);
	}
}

// Drive whichever window actually owns the page. When this embed runs inside
// an iframe we must navigate window.top so the destination takes over the
// tab instead of loading inside the widget frame.
function goToUrl(url) {
	try {
		if (window.self !== window.top && window.top) { window.top.location.href = url; return; }
	} catch (e) { /* cross-origin parent — fall through to own window */ }
	window.location.href = url;
}

// Resolve the OE Commerce storefront product page for a part and navigate
// there — same behaviour as the Liqui Moly embed and the parts-finder
// ResultsGrid (getStorefrontProductUrl). Falls back to the in-app
// parts-finder detail only if the storefront URL can't be resolved.
function navigateToPart(partId) {
	if (!partId) return;
	var fallbackUrl = buildPartsFinderUrl(partId);
	apiJson('/api/parts/' + encodeURIComponent(partId) + '/storefront-url', null, null)
		.then(function (data) {
			var url = (data && data.url) ? String(data.url).trim() : '';
			goToUrl(url || fallbackUrl);
		})
		.catch(function () { goToUrl(fallbackUrl); });
}

// ---------- fetching ----------

function doSearch(query) {
	var q = (query || '').trim();
	if (!q) { renderEmpty('Enter a vehicle above to see Wolf recommendations.'); return; }
	var reqId = ++_currentReqId;
	renderLoading();
	var path = '/api/oil/wolf/recommend?q=' + encodeURIComponent(q)
		+ '&locale=' + encodeURIComponent(_language)
		+ '&country=' + encodeURIComponent(_country);
	fetchJson(path).then(function (data) {
		if (reqId !== _currentReqId) return; // superseded
		// Capture the candidate list from this free-text response so the picker
		// survives a later candidate selection (≤1 candidate clears it).
		_candidates = (data && Array.isArray(data.candidates) && data.candidates.length > 1) ? data.candidates : [];
		var activeId = (data && data.typeId) || null;
		renderResponse(data || {}, activeId);
	}).catch(function (err) {
		if (reqId !== _currentReqId) return;
		renderMessage('error', 'Could not load Wolf recommendations. ' + (err && err.message ? '(' + err.message + ')' : ''));
	});
}

function doSearchByCandidate(candidate) {
	if (!candidate || !candidate.typeId || !candidate.searchId) return;
	var reqId = ++_currentReqId;
	renderLoading();
	var path = '/api/oil/wolf/recommend'
		+ '?typeId=' + encodeURIComponent(candidate.typeId)
		+ '&searchId=' + encodeURIComponent(candidate.searchId)
		+ '&candidateName=' + encodeURIComponent(candidate.name || '')
		+ '&locale=' + encodeURIComponent(_language)
		+ '&country=' + encodeURIComponent(_country);
	fetchJson(path).then(function (data) {
		if (reqId !== _currentReqId) return;
		renderResponse(data || {}, candidate.typeId);
	}).catch(function (err) {
		if (reqId !== _currentReqId) return;
		renderMessage('error', 'Could not load Wolf recommendations. ' + (err && err.message ? '(' + err.message + ')' : ''));
	});
}

// Throw on non-OK so doSearch/doSearchByCandidate can render an error notice
// (apiJson would swallow it into the fallback). apiFetch attaches the Bearer
// key + base URL for us.
function fetchJson(path) {
	return apiFetch(path).then(function (r) {
		if (!r.ok) throw new Error('HTTP ' + r.status);
		return r.json();
	});
}

// ---------- init ----------

export function init() {
	_host = findContainer();
	if (!_host) return; // not on a page with this widget

	if (_host.getAttribute('data-title')) _title = _host.getAttribute('data-title');
	if (_host.getAttribute('data-language')) _language = _host.getAttribute('data-language');
	if (_host.getAttribute('data-country')) _country = _host.getAttribute('data-country');

	_panel = buildPanel();
	_host.appendChild(_panel);

	// Wolf currently has no per-role gating — the recommendations aren't
	// price-sensitive — but load features so future flags (e.g. hiding the
	// widget for anonymous visitors) can be honoured without a code change.
	loadFeatures().then(function () {
		var initial = readVehicleContext();
		if (initial) {
			_searchInput.value = initial;
			doSearch(initial);
		} else {
			renderEmpty();
		}
	});
}

export var id = 'wolf-oil-guide';
